import { CircularProgress, Dialog } from '@mui/material';
import { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useDispatch, useSelector } from 'react-redux';
import { create_event, get_category, get_event } from '../Redux/Action/EventAction';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const CreateEventModal = ({ Open, setOpen }) => {
    const { t } = useTranslation('CreateEventModal');
    const dispatch = useDispatch();
    const { category } = useSelector((state) => state.Create);
    const [loading, setLoading] = useState(false);
    const [input, setinput] = useState({
        name: '',
        date: null,
        category_id: '',
    });

    const handleclose = () => {
        setOpen(false);
        // Reset form when closing
        setinput({
            name: '',
            date: null,
            category_id: '',
        });
    };

    useEffect(() => {
        dispatch(get_category());
    }, [dispatch]);

    const handlesubmit = (e) => {
        e.preventDefault();
        if (!input.name || !input.date || !input.category_id) {
            toast.error(t("fillAllFields"));
            return;
        }

        // Find the category by name to get its ID
        const selectedCategory = category?.data?.find((cat) => cat.name === input.category_id);
        if (selectedCategory) {
            setLoading(true);
            dispatch(create_event(
                input.name,
                dayjs(input.date).format('YYYY-MM-DD'),
                selectedCategory.id
            ))
                .then((result) => {
                    if (result && result.success) {
                        dispatch(get_event());
                        setinput({
                            name: '',
                            date: null,
                            category_id: '',
                        });
                        setOpen(false);
                        toast.success(t("successMessage"));
                    } else {
                        toast.error(t("errorMessage"));
                    }
                })
                .catch((error) => {
                    toast.error(t("errorMessage"));
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            toast.error(t("invalidCategory"));
        }
    };

    return (
        <Dialog
            open={Open}
            onClose={handleclose}
            PaperProps={{
                sx: {
                    borderRadius: "20px",
                    width: "410px",
                    maxWidth: "90vw",
                },
            }}
        >
            <form className='bg-white p-6 flex flex-col rounded-[20px]' onSubmit={handlesubmit}>
                <div className="grid gap-4 w-full">
                    <h2 className="text-xl font-semibold text-[#131415] text-left mb-2">
                        {t("create_event")}
                    </h2>

                    <div className="flex flex-col gap-2">
                        <span className='text-[14px] font-500 text-[#131415] text-left'>{t("nameLabel")}</span>
                        <input
                            type='text'
                            placeholder={t("namePlaceholder")}
                            className='border border-[#c3cbd7] py-[12px] rounded-[10px] px-3 text-[14px] w-full focus:outline-none focus:ring-2 focus:ring-[#3686FF] focus:border-transparent'
                            value={input.name}
                            name='name'
                            onChange={(e) => setinput({
                                ...input, [e.target.name]: e.target.value
                            })}
                            required
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className='text-[14px] font-500 text-[#131415] text-left'>{t("dateLabel")}</span>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={input.date}
                                onChange={(newValue) => setinput({ ...input, date: newValue })}
                                format="DD-MM-YYYY"
                                slotProps={{
                                    textField: {
                                        placeholder: t("selectDate"),
                                        InputProps: {
                                            style: {
                                                borderRadius: "10px",
                                                fontSize: "14px",
                                            },
                                        },
                                        sx: {
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: "10px",
                                                height: '49px',
                                                '& fieldset': {
                                                    borderColor: '#c3cbd7',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#3686FF',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#3686FF',
                                                    borderWidth: '2px',
                                                },
                                            },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>

                    <div className="flex flex-col gap-2">
                        <span className='text-[14px] font-[500] text-[#131415] text-left'>{t("categoryLabel")}</span>
                        <Select
                            className='text-[14px]'
                            value={input.category_id}
                            onChange={(e) => setinput({ ...input, category_id: e.target.value })}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) return t("selectCategory");
                                return selected;
                            }}
                            sx={{
                                borderRadius: '10px',
                                height: '49px',
                                fontSize: 14,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#c3cbd7',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#3686FF',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#3686FF',
                                    borderWidth: '2px',
                                }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        marginTop: '8px',
                                        borderRadius: '10px',
                                    },
                                },
                            }}
                            required
                        >
                            <MenuItem value="" disabled>
                                {t("selectCategory")}
                            </MenuItem>
                            {category.data && category.data.map((items) => (
                                <MenuItem key={items.id} value={items.name} sx={{ fontSize: 14 }}>
                                    {items.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>

                    <div className='w-full text-center mt-2 flex gap-3'>
                        <button
                            type='button'
                            onClick={handleclose}
                            className='border border-[#c3cbd7] text-[#131415] rounded-[10px] w-full py-[12px] text-[14px] hover:bg-gray-50 transition-colors'
                        >
                            {t("btn_cancel")}
                        </button>
                        <button
                            type='submit'
                            className='bg-[#3686FF] text-white rounded-[10px] w-full py-[12px] text-[14px] hover:bg-[#2a6fd8] transition-colors disabled:opacity-50'
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : t("btn_create")}
                        </button>
                    </div>
                </div>
            </form>
        </Dialog>
    );
};

export default CreateEventModal;